/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useStore } from "../../context/store/StoreContext";
import { useAuth } from "../../context/auth/AuthContext";
import { ALL_PERMISSIONS } from "../../hooks/useTenantStorePermissions";
import {
  fetchCurrentTenantPlan,
  invalidateTenantPlanCache,
  isPlanExpired,
} from "../../admin_store/utils/subscriptionPlan";
import { getOnboardingResumePath } from "../utils/onboardingSession";

/* ✅ Normalize Permissions */
const normalizePermissions = (apiUser) => {
  const map = {};
  Object.keys(apiUser).forEach((key) => {
    if (typeof apiUser[key] === "number") {
      const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
      map[camelKey] = apiUser[key];
    }
  });
  return map;
};

export default function useLogin(isArabic, navigate) {
  const { fetchStoreData } = useStore();
  const { login: authLogin, isAuthenticated, authRole: currentAuthRole, isLoading } = useAuth();

  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* 🔄 Auto Redirect if Logged In */
  useEffect(() => {
    if (!isLoading && isAuthenticated()) {
      if (currentAuthRole === "superAdmin") navigate("/admin");
      else if (currentAuthRole === "tenant") navigate(getOnboardingResumePath());
      else navigate("/");
    }
  }, [isLoading, isAuthenticated, currentAuthRole, navigate]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const show = {
    err: (m) => toast.error(m, { position: "top-center", rtl: isArabic, theme: "colored" }),
    ok: (m) => toast.success(m, { position: "top-center", rtl: isArabic, theme: "colored" }),
  };

  const clearAllData = () => localStorage.clear();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.email.trim() || !form.password.trim()) {
      show.err(isArabic ? "يرجى تعبئة جميع الحقول" : "Please fill in all fields");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email.trim())) {
      show.err(isArabic ? "البريد الإلكتروني غير صحيح" : "Invalid email format");
      return;
    }

    try {
      setLoading(true);

      const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

      const res = await fetch(`${baseUrl}/Login/UserLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

      /* ✅ Identify Login Type */
      const loginMessage = data?.message || "";
      const isEmployeeLogin = loginMessage.includes("Employee login successful");
      const isTenantLogin =
        loginMessage.includes("TenantUser login successful") ||
        loginMessage.includes("Tenant login successful");

      let user = null;
      let tenant = null;
      let tenantDetails = null;
      let authRole = null;
      let isTenantOwner = false;

      /* ✅ Super Admin */
      if (isEmployeeLogin) {
        user = data?.employee;
        authRole = "superAdmin";
      }

      /* ✅ Tenant */
      else if (isTenantLogin) {
        const loginEmail = form.email.toLowerCase().trim();
        const usersList = Array.isArray(data?.tenantUsersList) ? data.tenantUsersList : [];
        const getEmail = (u) => (u?.email || u?.Email || "").toLowerCase().trim();

        const matchedFromList = usersList.find((u) => getEmail(u) === loginEmail);
        const tenantUser = data?.tenantUser;
        const tenantUserMatches = tenantUser && getEmail(tenantUser) === loginEmail;

        // لا تعتمد على tenantUsersList[0] — اختر المستخدم المطابق للإيميل المدخل
        user = tenantUserMatches
          ? tenantUser
          : matchedFromList || tenantUser || data?.tenant;

        tenant = data?.tenant;
        tenantDetails = data?.tenantDetails;
        authRole = "tenant";

        const userFunctionId =
          user?.userFunctionId ??
          user?.userFunctionID ??
          user?.UserFunctionId ??
          user?.UserFunctionID;

        const emailMatches =
          tenant?.email &&
          getEmail(user) &&
          String(tenant.email).toLowerCase().trim() === getEmail(user);

        // المالك فقط إذا function=0/null أو إيميله يطابق إيميل التينانت
        isTenantOwner =
          userFunctionId === null ||
          userFunctionId === undefined ||
          Number(userFunctionId) === 0 ||
          emailMatches;
      }

      if (!user) throw new Error(isArabic ? "فشل تحميل المستخدم" : "Failed to load user");

      const userIsActive = user?.isActive === 1 || user?.IsActive === 1 || user?.isActive === true;
      if (!userIsActive)
        throw new Error(isArabic ? "الحساب غير نشط" : "Account is inactive");

      /* ✅ Permissions */
      let permissions = {};
      if (authRole === "superAdmin") {
        permissions = user;
      } else if (authRole === "tenant") {
        if (isTenantOwner) {
          permissions = ALL_PERMISSIONS;
        }
        else {
          permissions = normalizePermissions(user);
        }
      }

      /* ✅ Save Auth */
      authLogin({
        token: data.token,
        user,
        authRole,
        isTenantOwner,
        permissions,
      });
      /* =========================================
     🌟 SAVE FULL LOGIN RESPONSE TO LOCALSTORAGE
     ========================================= */
      localStorage.setItem("fullLoginResponse", JSON.stringify(data));

      // Token
      localStorage.setItem("token", data?.token || "");

      // Login Message
      localStorage.setItem("loginMessage", data?.message || "");

      // Tenant
      localStorage.setItem("tenant", JSON.stringify(data?.tenant || {}));

      // Tenant details
      localStorage.setItem("tenantDetails", JSON.stringify(data?.tenantDetails || {}));

      // Plans
      localStorage.setItem("plans", JSON.stringify(data?.plans || []));

      // Themes
      localStorage.setItem("themes", JSON.stringify(data?.themes || []));

      // Payment Methods
      localStorage.setItem("paymentMethods", JSON.stringify(data?.paymentMethods || []));

      // Tenant Users
      localStorage.setItem("tenantUsersList", JSON.stringify(data?.tenantUsersList || []));

      // 🔑 فقط للتاجر نحتاج tenantId
      if (authRole === "tenant") {
        const tenantIdToSave =
          user?.tenantId ||
          data?.tenant?.tenantId ||
          data?.tenantDetails?.tenantId ||
          data?.tenantUsersList?.[0]?.tenantId ||
          null;

        if (!tenantIdToSave) {
          throw new Error(isArabic ? "لم يتم تحديد هوية التاجر" : "Tenant ID not resolved");
        }

        localStorage.setItem("tenantId", String(tenantIdToSave));

        // Save Store Slug for Dashboard
        const slug = tenant?.storeSlug || tenant?.StoreSlug || tenantDetails?.storeSlug || tenantDetails?.StoreSlug || "";
        if (slug) localStorage.setItem("companyStoreSlug", slug);

        const storeName =
          tenantDetails?.clientName ||
          tenantDetails?.ClientName ||
          "";
        if (storeName) localStorage.setItem("companyStoreName", storeName);
      }



      /* ============================
         🚦 VALIDATE TENANT DATA
      =============================== */
      if (authRole === "tenant" && isTenantOwner) {
        tenantDetails = tenantDetails || {};
        const resumePath = getOnboardingResumePath(tenantDetails);
        if (resumePath !== "/admin-store") {
          show.ok(isArabic ? "تم تسجيل الدخول بنجاح 🎉" : "Login successful 🎉");
          setTimeout(() => navigate(resumePath), 600);
          return;
        }
      }

      /* ✅ Load Store + التحقق من صلاحية الخطة عبر GetTenantPlan.creationDate */
      let tenantPlanExpired = false;
      if (authRole === "tenant") {
        const tenantId =
          user?.tenantId ||
          data?.tenant?.tenantId ||
          data?.tenantDetails?.tenantId ||
          data?.tenantUsersList?.[0]?.tenantId;

        if (tenantId) {
          localStorage.setItem("tenantId", String(tenantId));
          await fetchStoreData(tenantId);

          try {
            invalidateTenantPlanCache();
            const tenantPlan = await fetchCurrentTenantPlan({ force: true });
            tenantPlanExpired = Boolean(tenantPlan && isPlanExpired(tenantPlan));
          } catch (err) {
            console.warn("Could not verify tenant plan from GetTenantPlan:", err);
          }
        }
      }


      show.ok(isArabic ? "تم تسجيل الدخول بنجاح 🎉" : "Login successful 🎉");

      setTimeout(() => {
        if (authRole === "superAdmin") navigate("/admin");
        else if (authRole === "tenant") navigate(tenantPlanExpired ? "/theme-plan" : "/admin-store");
        else navigate("/");
      }, 800);

    } catch (error) {
      let errorMessage = error.message;

      if (errorMessage.includes("fetch")) {
        errorMessage = isArabic ? "فشل الاتصال بالخادم" : "Connection failed";
      }

      show.err(errorMessage || (isArabic ? "فشل تسجيل الدخول" : "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return {
    form,
    loading,
    isMobile,
    handleChange,
    handleSubmit,
  };
}
